import './VehicleInfoPage.scss';
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from 'react-router-dom';
import { withAITracking } from '@microsoft/applicationinsights-react-js';
import { reactPlugin } from '../../../../services/appInsights.service';
import { ErrorLogger, ErrorSeverity } from '../../../../services/errorLogger.service';
import { VehicleInfoView } from '../../../../interfaces/responder.interface';
import VehicleDocumentsInfo from "../../appSubComponents/mobile/VehicleDocumentsInfo";
import { ResponderService } from '../../../../services/responder.service';
import { ToggleButton, ToggleButtonGroup } from 'react-bootstrap';
import { ArrowClockwise, GeoAltFill } from 'react-bootstrap-icons';
import Spinner from '../../../components/Spinner';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import moment from 'moment';


function VehicleInfo() {
  const location = useLocation();
  const vehicleDetails = (location.state as VehicleInfoView) || null;
  const searchVehicleType = (location.state as { searchVehicleType?: string })?.searchVehicleType || "Unique ID";

  const [tabIndex, setTabIndex] = useState(0);
  const [comment, setComment] = useState("");
  const [isLoadingLocation, setIsLoadingLocation] = useState(true);
  const [currentLatitude, setCurrentLatitude] = useState('');
  const [currentLongitude, setCurrentLongitude] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [disableYesButton, setDisableYesButton] = useState(false);

  const [incidentDate, setIncidentDate] = useState(new Date());
  const [incidentTime, setIncidentTime] = useState(new Date());

  const fromDocViewer = sessionStorage.getItem('returnFromIframe') === 'true';

  useEffect(() => {
    if (fromDocViewer) {
      setTabIndex(1);  //set tab to documents if coming from doc viewer
    }
    sessionStorage.setItem('licensePlateNumber', vehicleDetails.vehicle.licensePlateNumber);
    sessionStorage.setItem('uniqueNumber', vehicleDetails.vehicle.uniqueNumber);
    if (searchVehicleType === 'plate') {
      sessionStorage.removeItem('searchPdd');
      sessionStorage.setItem('searchPlate', searchVehicleType);
    }
    else if (searchVehicleType === 'pdd') {
      sessionStorage.removeItem('searchPlate');
      sessionStorage.setItem('searchPdd', searchVehicleType);
    }
    sessionStorage.removeItem('returnFromIframe');
  }, []);


  const navigate = useNavigate();

  // Extract vehicle data or use defaults
  const vin = vehicleDetails?.vehicle.vin || "N/A";
  const testerName = vehicleDetails?.vehicle.tester?.testerName || "N/A";
  const vehicleDisplay = vehicleDetails
    ? `${vehicleDetails.vehicle.year} ${vehicleDetails.vehicle.vehicleMake} ${vehicleDetails.vehicle.vehicleModel}`.trim()
    : "N/A";

  useEffect(() => {
    getCurrentLocation();
  }, []);

  const onSubmitClick = () => {
    setShowConfirmModal(true);
  };

  const onCancelClick = () => {
    setShowConfirmModal(false);
  };

  const onSuccessOk = () => {
    setShowSuccessModal(false);
    navigate(`/vehiclesearch/`, { state: { searchTerm: vehicleDetails.vehicle.licensePlateNumber, fromVehicleInfo: true } });
  };

  const onConfirmSubmit = async () => {
    setDisableYesButton(true);

    try {
      // Combine date and time for incident timestamp
      const combinedDateTime = new Date(incidentDate);
      combinedDateTime.setHours(incidentTime.getHours(), incidentTime.getMinutes());

      // Prepare incident data
      const incidentData = {
        incidentId: null,
        vehicleId: vehicleDetails.vehicle.vehicleId,
        latitude: currentLatitude,
        longitude: currentLongitude,
        statusCode: 'r',
        comment: comment,
        incidentDate: moment(combinedDateTime).format('YYYY-MM-DD HH:mm:ss.SSS')
      };

      // Submit incident to API
      const responderService = new ResponderService();
      await responderService.submitIncident(incidentData);

      // Reset form
      setShowConfirmModal(false);
      setComment('');
      setIncidentDate(new Date());
      setIncidentTime(new Date());
      setShowSuccessModal(true);

    } catch (error: any) {
      const errMsg = error.data?.developerMessage || error.message || 'Unable to submit incident. Please try again.';
      setErrorMessage(`We are unable to submit the Incident. Please try again. Error: ${errMsg}`);
      setShowErrorModal(true);
    } finally {
      setDisableYesButton(false);
    }
  };

  const getCurrentLocation = () => {
    setIsLoadingLocation(true);

    if (!navigator.geolocation) {
      setErrorMessage('Geolocation is not supported by this browser.');
      setShowErrorModal(true);
      setIsLoadingLocation(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setCurrentLatitude(position.coords.latitude.toString());
        setCurrentLongitude(position.coords.longitude.toString());
        setIsLoadingLocation(false);
      },
      (error) => {
        console.error('Error getting location:', error);
        ErrorLogger.get().logError(error, {
          component: 'VehicleInfo',
          action: 'getCurrentPosition',
          severity: ErrorSeverity.Warning,
          additionalData: {
            errorCode: error.code
          }
        });
        let errorMessage = 'Unable to get your location.';

        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access denied. Please enable location permissions.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable.';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out.';
            break;
        }
        setErrorMessage(errorMessage);
        setShowErrorModal(true);
        setIsLoadingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 30000,
        maximumAge: 0
      }
    );
  };

  const getVehicleTitle = () => {
    if (searchVehicleType === 'plate') return 'Vehicle';
    if (searchVehicleType === 'pdd') return 'PDD';
    return 'Vehicle Search';
  };

  return (
    <div className="vehicle-info-container">
      <div className="tabsContainer">
        <div className="vehicle-info-header">
          <div className="vehicle-info-title">
            <h5>{getVehicleTitle()}</h5>
          </div>
        </div>

        {/* Tab bar */}
        <div className="tabsContainerdiv">
          <ToggleButtonGroup
            className="vehicle-type w-100 mt-2"
            type="radio"
            value={tabIndex}
            name="vehicleTypeId">
            <ToggleButton id="vehicle-info-tab" className="vehicle-type-button" onChange={e => setTabIndex(0)} variant="light" value={0}>INFO</ToggleButton>
            <ToggleButton id="vehicle-documents-tab" className="vehicle-type-button" onChange={e => setTabIndex(1)} variant="light" value={1}>DOCUMENTS</ToggleButton>
          </ToggleButtonGroup>
        </div>

        {/* Vehicle Info Section */}
        <div className="vehicle-details-card mt-3">
          <div className="vehicle-info-row">
            <span className="info-label">VEHICLE</span>
            <span className="info-value">{vehicleDisplay}</span>
          </div>

          <div className="vehicle-info-row state-license-row">
            <div className="state-section">
              <span className="info-label">STATE</span>
              <span className="info-value">{vehicleDetails?.vehicle?.vehicleRegistrationStateCd || 'N/A'}</span>
            </div>
            <div className="license-section">
              <span className="info-label">LICENSE PLATE</span>
              <span className="info-value">{vehicleDetails?.vehicle?.licensePlateNumber || 'N/A'}</span>
            </div>
          </div>

          <div className="vehicle-info-row">
            <span className="info-label">VIN</span>
            <span className="info-value">{vin}</span>
          </div>

          <div className="vehicle-info-row">
            <span className="info-label">TESTER</span>
            <span className="info-value">{testerName}</span>
          </div>
        </div>

        <hr className="separator-hr" />


        {/* Location Status */}
        {tabIndex === 0 && (
          <div className="location-section" style={{ border: '1px solid #bdbbbbff', borderRadius: '6px', padding: '12px' }}>
            <div className="location-row">
              <div className="location-item">
                <div className="location-label">LATITUDE / LONGITUDE</div>
                <div className="location-value">
                  {(() => {
                    if (isLoadingLocation) {
                      return '';
                    }
                    if (currentLatitude !== "" && currentLongitude !== "") {
                      return `${currentLatitude} / ${currentLongitude}`;
                    }
                    return "";
                  })()}
                </div>
              </div>
            </div>

            {isLoadingLocation && <Spinner />}

            {!isLoadingLocation && (
              <div className="location-buttons-container">
                <button
                  className="refresh-location-btn"
                  onClick={getCurrentLocation}
                  title="Refresh location"
                >
                  <ArrowClockwise size={18} style={{ marginRight: '6px' }} /> REFRESH
                </button>
                {currentLatitude && currentLongitude && (
                  <button
                    className="view-location-btn"
                    onClick={() => window.open(`https://www.google.com/maps/search/?api=1&query=${currentLatitude},${currentLongitude}`, '_blank')}
                    title="View location on map"
                  >
                    <GeoAltFill size={18} style={{ marginRight: '6px' }} /> VIEW
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {tabIndex === 0 && (
          <div className="info-container">
            {vehicleDetails ? (
              <>
                <div className="info-header">
                  <div className="dateDiv">
                    <label htmlFor="incidentDate" className="dateLabel">DATE</label>
                    <DatePicker
                      id="incidentDate"
                      selected={incidentDate}
                      onChange={(date: Date | null) => setIncidentDate(date || new Date())}
                      dateFormat="MM/dd/yyyy"
                      className="dateInput"
                      wrapperClassName="w-100"
                      placeholderText="Select date"
                      withPortal
                      onFocus={(e) => e.target.blur()}
                    />
                  </div>
                  <div className="timeDiv">
                    <label htmlFor="incidentTime" className="timeLabel">TIME</label>
                    <DatePicker
                      id="incidentTime"
                      selected={incidentTime}
                      onChange={(time: Date | null) => setIncidentTime(time || new Date())}
                      showTimeSelect
                      showTimeSelectOnly
                      timeIntervals={1}
                      timeCaption="Time"
                      dateFormat="h:mm aa"
                      className="timeInput"
                      wrapperClassName="w-100"
                      placeholderText="Select time"
                      withPortal
                      portalId="time-picker-portal"
                      onFocus={(e) => e.target.blur()}
                    />
                  </div>
                </div>

                {/* Enlarged Comments Box */}
                <textarea
                  placeholder="Optional Comments"
                  value={comment}
                  onChange={e => setComment(e.target.value)}
                  className="comments-textarea"
                />
              </>
            ) : (
              <div className="no-data-message">
                No vehicle data available. Please search for a vehicle first.
              </div>
            )}

            <div className="submit-section">
              {!isLoadingLocation && (
                <button
                  type="button"
                  onClick={onSubmitClick}
                  disabled={disableYesButton || isLoadingLocation || currentLatitude === '' || currentLongitude === ''}
                  className='submit-button'
                >
                  SUBMIT INCIDENT DATA
                </button>
              )}
              {!isLoadingLocation && currentLatitude === '' && currentLongitude === '' && (
                <div className="location-error-message" style={{ color: 'red', marginTop: '10px', textAlign: 'center' }}>
                  Location access is required to submit incident data.
                </div>
              )}
            </div>

            {/* Modals */}
            {showConfirmModal && disableYesButton && (
              <Spinner />
            )}

            {/* Confirmation Modal */}
            {showConfirmModal && !disableYesButton && (
              <div className="modal-overlay">
                <div className="modal-content">
                  <div className="modal-header">
                    <h3>Are you sure you want to submit?</h3>
                  </div>
                  <div className="modal-body">

                    <div className="modal-buttons">
                      <div className="modal-div">
                        <button
                          className="confirm-button"
                          onClick={onConfirmSubmit}
                          disabled={disableYesButton}
                        >
                          YES, SUBMIT
                        </button>
                        <button
                          className="cancel-button"
                          onClick={onCancelClick}
                        >
                          CANCEL
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Success Modal */}
            {showSuccessModal && (
              <div className="modal-overlay">
                <div className="modal-content">
                  <div className="modal-header">
                    <h3>Your incident data has been successfully submitted.</h3>
                  </div>
                  <div className="modal-body">
                    <button
                      className="success-button"
                      onClick={onSuccessOk}
                    >
                      OK
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Error Modal */}
            {showErrorModal && (
              <div className="modal-overlay">
                <div className="modal-content error-modal">
                  <div className="modal-header error-header">
                    <h3>Error</h3>
                  </div>
                  <div className="modal-body">
                    <p className="error-message">{errorMessage}</p>
                    <button
                      className="error-button"
                      onClick={() => setShowErrorModal(false)}
                    >
                      OK
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {tabIndex === 1 && (
          <VehicleDocumentsInfo vehicleDocumentDetails={vehicleDetails} />
        )}
      </div>
    </div>
  );
}

export default withAITracking(reactPlugin, VehicleInfo);

